import './split-text.scss';
import gsap from 'gsap';
import SplitText from 'gsap/SplitText';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { retrieveScrollTriggerAttributes } from '../web-components-utils.js';

gsap.registerPlugin(SplitText);
gsap.registerPlugin(ScrollTrigger);

const template = document.createElement('template');
template.innerHTML = /* html */ `
    <slot></slot>
`;

class SplitTextElement extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.shadowRoot.appendChild(template.content.cloneNode(true));
    }

    connectedCallback() {
        setTimeout(() => {
            if (this.getAttribute('auto-init') == 'false') return;
            this.init();
        }, 0);
    }

    init() {
        this.el = this.children[0];
        this.splitAttr = this.getAttribute('split') || 'lines';
        this.delayAttr = this.getAttribute('delay') || 0;

        // pop to keep the last one as default
        this.animateAttr = this.getAttribute('animate') || this.splitAttr.split(',').pop().trim();

        // if overflow mode is true, will wrap elements
        this.overflowAttr =
            this.getAttribute('overflow') === 'false'
                ? null
                : this.getAttribute('overflow') || null;

        this.st_attr = retrieveScrollTriggerAttributes.call(this);

        // Store blur settings
        this.blurAttr = this.getAttribute('blur');

        document.fonts.ready.then(() => {
            // Initialize the split text
            this.createSplitText();

            // Set up resize handler
            this.debouncedResize = this.debounce(this.reflow.bind(this), 300);
            window.addEventListener('resize', this.debouncedResize);
        });
    }

    debounce(func, delay) {
        let timeout;
        return function (...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                func.apply(this, args);
            }, delay);
        };
    }

    reflow() {
        // Force opacity to 1 on the original element before anything else
        gsap.set(this.el, { opacity: 1, clearProps: 'filter,y' });

        // Kill previous tween and split text
        if (this.tween) {
            // Complete the tween to ensure final state is applied
            this.tween.progress(1);
            this.tween.kill();
            this.tween = null;
        }

        // Reset all possible animated elements to full opacity
        if (this.animateAttr === 'block') {
            gsap.set(this.split, { opacity: 1, clearProps: 'filter,y' });
        } else if (this.split && this.split[this.animateAttr]) {
            gsap.set(this.split[this.animateAttr], { opacity: 1, clearProps: 'filter,y' });
        }

        // Also reset any potential wrappers
        const lineWrappers = this.querySelectorAll('.line-wrapper');
        const wordWrappers = this.querySelectorAll('.word-wrapper');
        gsap.set([...lineWrappers, ...wordWrappers], { opacity: 1, clearProps: 'filter,y' });

        // Wait for GSAP to apply changes before reverting
        setTimeout(() => {
            if (this.split && this.split.revert) {
                this.split.revert();
            }

            // Ensure original element is fully visible after revert
            gsap.set(this.el, { opacity: 1, clearProps: 'filter,y' });

            // Recreate the split text and animation
            this.createSplitText();
            ScrollTrigger.refresh();
        }, 10);
    }

    createSplitText() {
        // Create new split text instance
        if (this.splitAttr !== 'none') {
            this.split = new SplitText(this.el, {
                type: this.splitAttr,
                charsClass: 'char',
                wordsClass: 'word',
                linesClass: 'line',
            });
        } else {
            this.split = this.el;
        }

        // OPTIONAL BLUR EFFECT
        let blur = '';

        let stagger = 0.15;
        if (this.splitAttr.includes('chars') || this.animateAttr === 'chars') stagger = 0.01;

        // Animate the whole block at once
        if (this.animateAttr === 'block') stagger = 0;

        // Option - Filter blur (default value : 10px)
        let opacity = 0;
        if (this.blurAttr !== null) {
            if (this.blurAttr !== '') {
                blur = 'blur(' + this.blurAttr + 'px)';
            } else {
                blur = 'blur(10px)';
            }
        }

        const tweenParams = {
            y: this.animateAttr === 'block' ? '20%' : '100%', // Lessen the distance for the block animation
            opacity: opacity,
            filter: blur || '',
            stagger: stagger,
            duration: 0.4,
            delay: this.delayAttr,
            ease: 'circ.out',
        };

        if (!this.st_attr.disable) {
            tweenParams.scrollTrigger = {
                scroller: this.st_attr.scroller || window,
                trigger: this.st_attr.trigger || this,
                endTrigger: this.st_attr.endTrigger || null,
                start: this.st_attr.start || 'top 75%',
                end: this.st_attr.end || 'top 70%',
                toggleActions: 'play none none none',
                scrub: this.st_attr.scrub || null,
                // markers: true,
            };
        }

        /********* OVERFLOW ANIMATION REQUIREMENT : WRAP ELEMENTS IN A DIV THAT HAS OVERFLOW:HIDDEN ********/
        // Note: If you want to animate characters (with a stagger) while keeping the overflow:hidden effect,
        // your component would look like this :   <split-text split="chars, lines" animate="chars">
        this.wrapSplitElements = () => {
            // Clean up previous wrappers before creating new ones
            const cleanupPreviousWrappers = (wrapperClass) => {
                const existingWrappers = this.querySelectorAll(`.${wrapperClass}`);
                existingWrappers.forEach((wrapper) => {
                    // Move all children out of the wrapper back to the parent
                    while (wrapper.firstChild) {
                        wrapper.parentNode.insertBefore(wrapper.firstChild, wrapper);
                    }
                    // Remove the empty wrapper
                    wrapper.parentNode.removeChild(wrapper);
                });
            };

            // Always clean up both types of wrappers on resize
            cleanupPreviousWrappers('line-wrapper');
            cleanupPreviousWrappers('word-wrapper');

            if (this.animateAttr === 'lines' && this.split.lines) {
                this.split.lines.map((line) => {
                    const wrapEl = document.createElement('div');
                    wrapEl.classList.add('line-wrapper');
                    line.parentNode.appendChild(wrapEl);
                    wrapEl.appendChild(line);
                });
            }

            if (this.animateAttr === 'words' && this.split.words) {
                this.split.words.map((word) => {
                    const wrapEl = document.createElement('div');
                    wrapEl.classList.add('word-wrapper');
                    word.parentNode.appendChild(wrapEl);
                    wrapEl.appendChild(word);
                });
            }
        };

        if (this.overflowAttr) {
            this.wrapSplitElements();
        }

        if (this.animateAttr === 'block') {
            this.tween = gsap.from(this.split, tweenParams);
        } else {
            this.tween = gsap.from(this.split[this.animateAttr], tweenParams);
        }

        // Set the component to be visible
        gsap.set('split-text', { opacity: 1 });

        // Ensure initial state of animated elements is properly set
        if (this.st_attr.disable) {
            // If animations are disabled, ensure everything is fully visible
            if (this.animateAttr === 'block') {
                gsap.set(this.split, { opacity: 1, clearProps: 'filter,y' });
            } else if (this.split[this.animateAttr]) {
                gsap.set(this.split[this.animateAttr], { opacity: 1, clearProps: 'filter,y' });
            }
        } else {
            // Set initial state for animation
            if (this.animateAttr === 'block') {
                gsap.set(this.split, { opacity: opacity, filter: blur || '', y: tweenParams.y });
            } else if (this.split[this.animateAttr]) {
                gsap.set(this.split[this.animateAttr], {
                    opacity: opacity,
                    filter: blur || '',
                    y: tweenParams.y,
                });
            }
        }

        if (this.st_attr.disable) {
            this.tween.pause();

            // If animations are disabled, ensure everything is fully visible
            if (this.animateAttr === 'block') {
                gsap.set(this.split, { opacity: 1, clearProps: 'filter,y' });
            } else if (this.split[this.animateAttr]) {
                gsap.set(this.split[this.animateAttr], { opacity: 1, clearProps: 'filter,y' });
            }
        } else {
            // Add onComplete callback to ensure opacity is 1 when animation finishes
            this.tween.eventCallback('onComplete', () => {
                if (this.animateAttr === 'block') {
                    gsap.set(this.split, { opacity: 1, clearProps: 'filter' });
                } else if (this.split[this.animateAttr]) {
                    gsap.set(this.split[this.animateAttr], { opacity: 1, clearProps: 'filter' });
                }
            });
        }
    }

    disconnectedCallback() {
        if (this.tween) {
            // Complete the tween to ensure final state is applied
            this.tween.progress(1);
            this.tween.kill();
            this.tween = null;
        }

        // Ensure all elements are fully visible before disconnecting
        gsap.set(this.el, { opacity: 1, clearProps: 'filter,y' });

        if (this.split && this.split.revert) {
            this.split.revert();
        }

        if (this.debouncedResize) {
            window.removeEventListener('resize', this.debouncedResize);
        }
    }
}

window.customElements.define('split-text', SplitTextElement);
